import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  approvalStatusClass,
  approvalStatusLabel,
  formatDay,
  formatMoney,
  listInvoiceApprovalApprovers,
  listInvoiceApprovals,
  type InvoiceApprovalApprover,
  type InvoiceApprovalQueueItem,
  type InvoiceApprovalScope,
} from "@/lib/invoiceApproval";
import { ApprovalDialog } from "@/pages/invoiceApproval/ApprovalDialog";
import { InvoiceThumb } from "@/pages/invoiceApproval/InvoiceThumb";

const SCOPES: Array<{ id: InvoiceApprovalScope; label: string }> = [
  { id: "mine", label: "Mir vorliegend" },
  { id: "offen", label: "Offen" },
  { id: "klaerung", label: "In Klärung" },
  { id: "zweite", label: "Zweite Freigabe" },
  { id: "erledigt", label: "Erledigt" },
  { id: "alle", label: "Alle" },
];

export function InvoiceApprovalIndexPage() {
  const [scope, setScope] = useState<InvoiceApprovalScope>("mine");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<InvoiceApprovalQueueItem[]>([]);
  const [approvers, setApprovers] = useState<InvoiceApprovalApprover[]>([]);
  const [threshold, setThreshold] = useState(5000);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listInvoiceApprovals(scope);
      setItems(res.items || []);
      setThreshold(res.four_eyes_threshold || 5000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Die Freigaben konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void listInvoiceApprovalApprovers()
      .then((res) => {
        setApprovers(res.items || []);
        if (res.four_eyes_threshold) setThreshold(res.four_eyes_threshold);
      })
      .catch(() => setApprovers([]));
  }, []);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) => {
      const blob = [
        item.header.supplier_name,
        item.header.invoice_number,
        item.subject,
        item.from_name,
        item.assignee_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return blob.includes(needle);
    });
  }, [items, query]);

  return (
    <AppShell
      title="Rechnungsfreigabe"
      subtitle="Belege prüfen, Preisabweichungen sehen und freigeben, klären oder ablehnen."
    >
      <div className="space-y-4">
        <Alert variant="info">
          Hier liegen die Rechnungen, die Ihnen zugewiesen sind. Ab {formatMoney(threshold)} Brutto
          braucht es eine zweite Person. Die Zuweisung starten Sie im{" "}
          <Link to="/bestellwesen/rechnungs-postfach" className="font-medium underline">
            Rechnungs-Postfach
          </Link>
          , der Vorschlag kommt aus dem Kürzel der TK-Nummer.
        </Alert>

        <div className="flex flex-wrap items-center gap-2">
          {SCOPES.map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => setScope(entry.id)}
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                scope === entry.id
                  ? "bg-accent text-white"
                  : "border border-separator text-label-secondary hover:bg-card"
              }`}
            >
              {entry.label}
            </button>
          ))}
          <input
            className="ml-auto min-w-48 rounded border border-separator bg-card px-3 py-1.5 text-sm"
            placeholder="Lieferant, Nummer, Betreff"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>

        {error ? <Alert variant="error">{error}</Alert> : null}
        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : visible.length === 0 ? (
          <p className="rounded-xl border border-separator bg-card px-4 py-10 text-center text-sm text-label-secondary">
            Keine Rechnungen in dieser Ansicht.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
            {visible.map((item) => {
              const supplier = item.header.supplier_name || item.from_name || "Unbekannter Absender";
              return (
                <button
                  key={item.candidate_id}
                  type="button"
                  onClick={() => setOpenId(item.candidate_id)}
                  className="overflow-hidden rounded-xl border border-separator bg-card text-left shadow-sm transition hover:border-accent/40"
                >
                  <div className="relative aspect-[3/4] overflow-hidden bg-white">
                    <InvoiceThumb
                      candidateId={item.candidate_id}
                      preview={item.preview}
                      label={supplier}
                    />
                    <span className={`absolute right-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-semibold ${approvalStatusClass(item.status)}`}>
                      {approvalStatusLabel(item.status)}
                    </span>
                    <div className="absolute left-2 top-2 flex flex-col gap-1">
                      {item.price_deviation_count > 0 ? (
                        <span className="rounded-full bg-status-red px-2 py-0.5 text-[10px] font-semibold text-white">
                          Preis {item.price_deviation_count}
                        </span>
                      ) : null}
                      {item.duplicate ? (
                        <span className="rounded-full bg-status-orange px-2 py-0.5 text-[10px] font-semibold text-white">
                          Dublette
                        </span>
                      ) : null}
                      {item.header.skonto ? (
                        <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold text-white">
                          Skonto
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="space-y-0.5 px-2.5 py-2">
                    <div className="truncate text-sm font-medium">{supplier}</div>
                    <div className="truncate font-mono text-[11px] text-label-secondary">
                      {item.header.invoice_number || item.subject || "ohne Nummer"}
                    </div>
                    <div className="text-sm font-semibold tabular-nums">
                      {formatMoney(item.header.total_gross, item.header.currency || "EUR")}
                    </div>
                    <div className="text-[11px] text-label-tertiary">
                      {formatDay(item.header.invoice_date || item.received_at)}
                      {item.project_count > 0 ? ` · ${item.project_count} Projekt${item.project_count === 1 ? "" : "e"}` : ""}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <ApprovalDialog
        candidateId={openId}
        approvers={approvers}
        onClose={() => setOpenId(null)}
        onChanged={() => void load()}
      />
    </AppShell>
  );
}
